/**
 * ModerationActionModal — unified admin moderation flow.
 *
 * Opens from either the reports queue (with a report + target user context)
 * or from the User Management portal (target user only). Admin ticks any
 * subset of: 24h soft-ban, DM the user, post a public thread reply, pin a
 * moderator note. Bodies are pre-filled from `/admin/moderation-actions/
 * templates?reason=…` and are editable before send.
 *
 * Props:
 *   open       : boolean
 *   onClose    : () => void
 *   targetUser : { id, email, display_name?, role? }   REQUIRED
 *   report?    : { id, reason?, topic_id?, reply_id? } optional
 *   topicId?   : string   fallback if the report doesn't include one
 *   onDone?    : (outcomes) => void
 */
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { AuthContext } from '../../App';
import { Button } from '../ui/button';
import {
  ShieldAlert, X, MessageCircle, MessagesSquare, Pin, Ban, Loader2, Info,
} from 'lucide-react';

const REASONS = [
  { value: 'spam',           label: 'Spam / promotional' },
  { value: 'sexual',         label: 'Sexual / graphic' },
  { value: 'violence',       label: 'Violence / threats' },
  { value: 'harassment',     label: 'Harassment' },
  { value: 'misinformation', label: 'Misinformation' },
  { value: 'off_topic',      label: 'Off-topic' },
  { value: 'default',        label: 'Other guideline violation' },
];

const ModerationActionModal = ({ open, onClose, targetUser, report, topicId, onDone }) => {
  const { API } = useContext(AuthContext);
  const [reason, setReason] = useState((report?.reason || 'spam').toLowerCase());

  // Action toggles
  const [doSoftBan,   setDoSoftBan]   = useState(true);
  const [doDM,        setDoDM]        = useState(true);
  const [doReply,     setDoReply]     = useState(!!(report?.topic_id || topicId));
  const [doPin,       setDoPin]       = useState(false);

  // Bodies
  const [dmBody,     setDMBody]     = useState('');
  const [replyBody,  setReplyBody]  = useState('');
  const [pinBody,    setPinBody]    = useState('');

  const [templates, setTemplates] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [suggestion, setSuggestion] = useState(null); // {tier, label, reason, offenses_30d}

  const targetTopicId = report?.topic_id || topicId;

  // --- Fetch templates on open (and whenever reason changes) --------------
  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/admin/moderation-actions/templates`, { params: { reason } });
        if (cancelled) return;
        setTemplates(r.data);
        // Pre-fill only if the field is still empty OR still matches the
        // previously-loaded template (so admin edits aren't clobbered).
        setDMBody((prev) => (!prev || prev === templates?.dm_template) ? r.data.dm_template : prev);
        setReplyBody((prev) => (!prev || prev === templates?.thread_reply_template) ? r.data.thread_reply_template : prev);
      } catch (e) {
        // silent — the user can still type free-form
      }
    })();
    return () => { cancelled = true; };
  }, [open, reason]);

  // --- Fetch the auto-escalation suggestion once per open -----------------
  useEffect(() => {
    if (!open || !targetUser?.id) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/admin/users/${targetUser.id}`);
        if (cancelled) return;
        setSuggestion({
          ...(r.data.suggested_action || {}),
          offenses_30d: r.data.offenses_30d || 0,
        });
      } catch (e) {
        // silent — the modal still works without the suggestion
      }
    })();
    return () => { cancelled = true; };
  }, [open, targetUser?.id]);

  // Reset on close
  useEffect(() => {
    if (!open) {
      setSubmitting(false);
    }
  }, [open]);

  const canReplyOrPin = !!targetTopicId;

  const disabled = useMemo(() => {
    if (submitting) return true;
    if (!targetUser?.id) return true;
    if (!(doSoftBan || doDM || doReply || doPin)) return true;
    if (doReply && !replyBody.trim()) return true;
    if (doDM    && !dmBody.trim())    return true;
    if (doPin   && !pinBody.trim())   return true;
    if ((doReply || doPin) && !canReplyOrPin) return true;
    return false;
  }, [submitting, targetUser, doSoftBan, doDM, doReply, doPin, replyBody, dmBody, pinBody, canReplyOrPin]);

  if (!open) return null;

  const submit = async () => {
    setSubmitting(true);
    try {
      const payload = {
        target_user_id:      targetUser.id,
        report_id:           report?.id || null,
        reason,
        soft_ban:            doSoftBan,
        dm_user:             doDM,
        dm_body:             doDM ? dmBody : null,
        post_thread_reply:   doReply,
        thread_reply_body:   doReply ? replyBody : null,
        pin_moderator_note:  doPin,
        pin_note_body:       doPin ? pinBody : null,
        topic_id:            (doReply || doPin) ? targetTopicId : null,
      };
      const r = await axios.post(`${API}/admin/moderation-actions/execute`, payload);
      toast.success('Moderation action applied.');
      onDone && onDone(r.data.outcomes);
      onClose && onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not apply the moderation action.');
    } finally {
      setSubmitting(false);
    }
  };

  return createPortal((
    <div
      className="fixed inset-0 z-[130] bg-black/60 flex items-center justify-center p-4"
      data-testid="moderation-action-modal"
      onClick={onClose}
    >
      <div
        className="w-full max-w-2xl rounded-2xl bg-card border-2 border-rose-500/30 shadow-2xl p-5 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start gap-2">
          <div className="h-9 w-9 rounded-xl bg-rose-500/15 text-rose-600 flex items-center justify-center flex-shrink-0">
            <ShieldAlert className="w-4 h-4" />
          </div>
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-widest font-bold text-rose-600">Moderation action</div>
            <h3 className="font-heading text-base font-extrabold text-foreground">
              Take action on {targetUser?.display_name || targetUser?.email || 'user'}
            </h3>
            <p className="text-xs text-muted-foreground mt-0.5">
              Pick any combination below. All chosen actions run together in one atomic call,
              and the underlying report (if any) is auto-marked as <em>actioned</em>.
            </p>
          </div>
          <button
            type="button" onClick={onClose}
            className="ml-auto h-7 w-7 rounded-full hover:bg-muted flex items-center justify-center text-muted-foreground"
            aria-label="Close" data-testid="mod-action-close-btn"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Auto-escalation suggestion */}
        {suggestion && targetUser?.role !== 'admin' && (
          <div
            className="mt-4 rounded-xl border-2 border-amber-500/40 bg-amber-500/10 p-3 flex items-start gap-2"
            data-testid="mod-action-suggestion"
          >
            <ShieldAlert className="w-4 h-4 mt-0.5 text-amber-700 flex-shrink-0" />
            <div className="flex-1 min-w-0 text-xs">
              <div className="font-bold text-amber-800 uppercase tracking-widest text-[10px]">Recommended next tier</div>
              <div className="mt-0.5 font-heading text-base font-extrabold text-foreground">{suggestion.label}</div>
              <div className="text-muted-foreground">{suggestion.reason}</div>
            </div>
          </div>
        )}

        {/* Reason picker */}
        <div className="mt-4">
          <label className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Violation category</label>
          <select
            value={reason}
            onChange={(e) => { setReason(e.target.value); setDMBody(''); setReplyBody(''); }}
            className="mt-1 w-full h-10 rounded-lg border border-input bg-background px-3 text-sm"
            data-testid="mod-action-reason"
          >
            {REASONS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
          </select>
        </div>

        {/* Action: soft-ban */}
        <label className="mt-4 flex items-start gap-2 rounded-xl border border-border p-3 cursor-pointer">
          <input
            type="checkbox" checked={doSoftBan}
            onChange={(e) => setDoSoftBan(e.target.checked)}
            className="mt-0.5 h-4 w-4"
            data-testid="mod-action-softban-checkbox"
          />
          <span className="flex-1">
            <span className="font-semibold text-foreground flex items-center gap-1.5">
              <Ban className="w-3.5 h-3.5" /> Soft-ban for 24 hours
            </span>
            <span className="block text-xs text-muted-foreground">
              Sets status to <code>read_only</code> for 24h. Member can browse but cannot post, comment, or DM.
              A red banner appears on their dashboard explaining the hold. Auto-expires; admins can lift early.
            </span>
          </span>
        </label>

        {/* Action: DM user */}
        <label className="mt-2 flex items-start gap-2 rounded-xl border border-border p-3 cursor-pointer">
          <input
            type="checkbox" checked={doDM}
            onChange={(e) => setDoDM(e.target.checked)}
            className="mt-0.5 h-4 w-4"
            data-testid="mod-action-dm-checkbox"
          />
          <span className="flex-1">
            <span className="font-semibold text-foreground flex items-center gap-1.5">
              <MessageCircle className="w-3.5 h-3.5" /> DM the user (private notice)
            </span>
            <span className="block text-xs text-muted-foreground">
              Sends a private message from your admin account explaining the violation.
            </span>
            {doDM && (
              <textarea
                rows={4}
                value={dmBody}
                onChange={(e) => setDMBody(e.target.value)}
                placeholder={templates?.dm_template || 'Loading template…'}
                className="mt-2 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
                data-testid="mod-action-dm-body"
              />
            )}
          </span>
        </label>

        {/* Action: public admin reply */}
        <label className={`mt-2 flex items-start gap-2 rounded-xl border p-3 ${canReplyOrPin ? 'border-border cursor-pointer' : 'border-dashed border-border opacity-60'}`}>
          <input
            type="checkbox" checked={doReply && canReplyOrPin}
            onChange={(e) => setDoReply(e.target.checked)}
            disabled={!canReplyOrPin}
            className="mt-0.5 h-4 w-4"
            data-testid="mod-action-reply-checkbox"
          />
          <span className="flex-1">
            <span className="font-semibold text-foreground flex items-center gap-1.5">
              <MessagesSquare className="w-3.5 h-3.5" /> Post a public admin reply on the thread
            </span>
            <span className="block text-xs text-muted-foreground">
              Shows a red <strong>🛡 Admin action</strong> badge in-thread so the community sees action was taken.
              {!canReplyOrPin && ' (No topic in scope — open this modal from a thread report to enable.)'}
            </span>
            {doReply && canReplyOrPin && (
              <textarea
                rows={3}
                value={replyBody}
                onChange={(e) => setReplyBody(e.target.value)}
                placeholder={templates?.thread_reply_template || 'Loading template…'}
                className="mt-2 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
                data-testid="mod-action-reply-body"
              />
            )}
          </span>
        </label>

        {/* Action: pin a moderator note */}
        <label className={`mt-2 flex items-start gap-2 rounded-xl border p-3 ${canReplyOrPin ? 'border-border cursor-pointer' : 'border-dashed border-border opacity-60'}`}>
          <input
            type="checkbox" checked={doPin && canReplyOrPin}
            onChange={(e) => setDoPin(e.target.checked)}
            disabled={!canReplyOrPin}
            className="mt-0.5 h-4 w-4"
            data-testid="mod-action-pin-checkbox"
          />
          <span className="flex-1">
            <span className="font-semibold text-foreground flex items-center gap-1.5">
              <Pin className="w-3.5 h-3.5" /> Pin a sticky moderator note
            </span>
            <span className="block text-xs text-muted-foreground">
              Anchors a short note at the top of the topic so everyone reading sees the moderator&rsquo;s position.
            </span>
            {doPin && canReplyOrPin && (
              <textarea
                rows={2}
                value={pinBody}
                onChange={(e) => setPinBody(e.target.value)}
                placeholder="e.g. Please keep this thread on-topic — no personal attacks."
                className="mt-2 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
                data-testid="mod-action-pin-body"
              />
            )}
          </span>
        </label>

        <div className="mt-4 flex items-center gap-2 text-xs text-muted-foreground bg-muted/40 rounded-lg px-3 py-2">
          <Info className="w-3.5 h-3.5 flex-shrink-0" />
          <span>
            All actions are logged to <code>moderation_log</code>. If a report ID is
            attached ({report?.id ? <><code>#{String(report.id).slice(-6)}</code></> : 'none in scope'})
            it&rsquo;s auto-marked as <em>actioned</em> and disappears from the open queue.
          </span>
        </div>

        <div className="mt-4 flex items-center justify-end gap-2">
          <Button variant="outline" onClick={onClose} className="rounded-full font-bold">
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={disabled}
            className="rounded-full font-bold bg-rose-600 text-white hover:bg-rose-700"
            data-testid="mod-action-submit-btn"
          >
            {submitting
              ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Applying…</>
              : 'Apply selected actions'}
          </Button>
        </div>
      </div>
    </div>
  ), document.body);
};

export default ModerationActionModal;
